import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  AgentReportSection,
  AssuranceCommandCenter as AssuranceCommandCenterData,
  Localized,
} from '@servicesai/api-client';
import { GlassCard, StatusPill, useI18n } from '@servicesai/ui';
import { admin } from '../../lib/api.js';

const ASSURANCE_SLUGS = new Set([
  'cyber-sentinel',
  'ai-governance',
  'tech-ops',
  'language-sentinel',
]);

type TrendWindowMinutes = 1_440 | 10_080 | 43_200;

const TREND_WINDOWS: { minutes: TrendWindowMinutes; label: Localized }[] = [
  { minutes: 1_440, label: { en: '24 hours', ar: '٢٤ ساعة' } },
  { minutes: 10_080, label: { en: '7 days', ar: '٧ أيام' } },
  { minutes: 43_200, label: { en: '30 days', ar: '٣٠ يوماً' } },
];

export function isAssuranceWorkspace(slug: string, labels: string[]): boolean {
  return labels.includes('assurance') || ASSURANCE_SLUGS.has(slug);
}

function valueLabel(value: unknown, lang: 'en' | 'ar'): string {
  if (value === null || value === undefined) return lang === 'ar' ? 'غير مقاس' : 'Not measured';
  if (typeof value === 'number') {
    return new Intl.NumberFormat(lang === 'ar' ? 'ar-AE' : 'en-GB', {
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (typeof value === 'string' || typeof value === 'boolean') return String(value);
  return lang === 'ar' ? 'متاح في التفاصيل' : 'Available in details';
}

function isLocalized(value: unknown): value is Localized {
  return Boolean(
    value
    && typeof value === 'object'
    && typeof (value as { en?: unknown }).en === 'string'
    && typeof (value as { ar?: unknown }).ar === 'string',
  );
}

function localizedItems(value: unknown): Localized[] {
  return Array.isArray(value) ? value.filter(isLocalized) : [];
}

function windowMeasurement(
  reports: AssuranceCommandCenterData['reports'],
  windowMinutes: TrendWindowMinutes,
): { label: Localized; value: unknown } | null {
  for (const report of reports) {
    for (const binding of report.definition.bindings) {
      if (binding.windowMinutes !== windowMinutes || binding.aggregation !== 'count') continue;
      const section = report.definition.sections.find(
        (candidate): candidate is Extract<AgentReportSection, { kind: 'metric' }> =>
          candidate.kind === 'metric' && candidate.bindingKey === binding.key,
      );
      if (section && report.resolved[binding.key] !== null && report.resolved[binding.key] !== undefined) {
        return { label: section.label, value: report.resolved[binding.key] };
      }
    }
  }
  return null;
}

function Trend({
  rows,
  generatedAt,
  windowMinutes,
}: {
  rows: { bucket: string; count: number }[];
  generatedAt: string;
  windowMinutes: TrendWindowMinutes;
}) {
  const { l, lang } = useI18n();
  const anchor = Date.parse(generatedAt);
  const cutoff = anchor - windowMinutes * 60_000;
  const visibleRows = Number.isFinite(anchor)
    ? rows.filter((row) => {
        const timestamp = Date.parse(row.bucket);
        return Number.isFinite(timestamp) && timestamp >= cutoff && timestamp <= anchor;
      })
    : [];
  if (visibleRows.length === 0) {
    return <p className="py-8 text-center text-xs text-ink-faint">{l({ en: 'No measured trend points for this window.', ar: 'لا توجد نقاط اتجاه مقاسة لهذه النافذة.' })}</p>;
  }
  const width = 520;
  const height = 120;
  const max = Math.max(1, ...visibleRows.map((row) => row.count));
  const points = visibleRows.map((row, index) => {
    const x = (index / Math.max(1, visibleRows.length - 1)) * width;
    const y = height - 14 - (row.count / max) * (height - 28);
    return `${x},${y}`;
  }).join(' ');
  return (
    <figure>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={l({ en: 'Measured assurance signal trend', ar: 'اتجاه إشارات الضمان المقاسة' })}
        className="h-32 w-full"
      >
        <path d={`M0 ${height - 14} H${width}`} stroke="#d8dde3" />
        <polyline points={points} fill="none" stroke="#0173ab" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        {visibleRows.map((row, index) => {
          const [cx, cy] = points.split(' ')[index]!.split(',');
          return (
            <circle key={`${row.bucket}-${index}`} cx={cx} cy={cy} r="3" fill="#b68a35">
              <title>{`${new Date(row.bucket).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}: ${row.count}`}</title>
            </circle>
          );
        })}
      </svg>
      <figcaption className="sr-only">
        {visibleRows.map((row) => `${row.bucket}: ${row.count}`).join('; ')}
      </figcaption>
    </figure>
  );
}

function SectionContext({
  section,
  resolved,
}: {
  section: Extract<AgentReportSection, { kind: 'narrative' | 'callout' }>;
  resolved: Record<string, unknown>;
}) {
  const { l, lang } = useI18n();
  const values = section.bindingKeys
    .filter((key) => resolved[key] !== null && resolved[key] !== undefined)
    .map((key) => `${key.replaceAll('_', ' ')}: ${valueLabel(resolved[key], lang)}`);
  return (
    <div className={[
      'rounded-lg border p-3',
      section.kind === 'callout' && section.tone === 'critical'
        ? 'border-aered-200 bg-aered-50'
        : section.kind === 'callout' && section.tone === 'warning'
          ? 'border-aegold-300 bg-aegold-50'
          : 'border-aeblack-100 bg-aeblack-50',
    ].join(' ')}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-extrabold">
          {section.title ? l(section.title) : l({ en: 'Report context', ar: 'سياق التقرير' })}
        </h4>
        {section.kind === 'callout' && (
          <span className="text-[10px] font-bold uppercase text-ink-faint">{section.tone}</span>
        )}
      </div>
      <p className="mt-1 text-xs text-ink-soft">{l(section.content)}</p>
      {values.length > 0 && <p className="mt-2 font-mono text-[10px] text-ink-faint">{values.join(' · ')}</p>}
    </div>
  );
}

function ReportDefinitionView({
  report,
}: {
  report: AssuranceCommandCenterData['reports'][number];
}) {
  const { l, lang } = useI18n();
  const metrics = report.definition.sections.filter(
    (section): section is Extract<AgentReportSection, { kind: 'metric' }> =>
      section.kind === 'metric',
  );
  const tables = report.definition.sections.filter(
    (section): section is Extract<AgentReportSection, { kind: 'table' }> =>
      section.kind === 'table',
  );
  const contexts = report.definition.sections.filter(
    (section): section is Extract<AgentReportSection, { kind: 'narrative' | 'callout' }> =>
      section.kind === 'narrative' || section.kind === 'callout',
  );
  const links = report.definition.sections.filter(
    (section): section is Extract<AgentReportSection, { kind: 'cta' }> =>
      section.kind === 'cta',
  );
  return (
    <GlassCard className="p-4" strong>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-extrabold">{l(report.definition.identity.title)}</h3>
          {report.definition.identity.description && (
            <p className="mt-1 text-xs text-ink-faint">{l(report.definition.identity.description)}</p>
          )}
        </div>
        <span className="text-[10px] text-ink-faint">
          {new Date(report.generatedAt).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}
        </span>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((section) => (
          <div key={section.bindingKey} className="rounded-lg border border-aeblack-100 bg-white p-3">
            <div className="text-[10px] font-bold uppercase text-ink-faint">{l(section.label)}</div>
            <div className="mt-1 font-mono text-xl font-extrabold">
              {valueLabel(report.resolved[section.bindingKey], lang)}
            </div>
          </div>
        ))}
      </div>
      {contexts.length > 0 && (
        <div className="mt-4 grid gap-2 lg:grid-cols-2">
          {contexts.map((section, index) => (
            <SectionContext key={section.id ?? `${section.kind}-${index}`} section={section} resolved={report.resolved} />
          ))}
        </div>
      )}
      {tables.map((section) => {
        const rows = Array.isArray(report.resolved[section.bindingKey])
          ? report.resolved[section.bindingKey] as Record<string, unknown>[]
          : [];
        return (
          <div key={section.bindingKey} className="mt-4 overflow-x-auto">
            <h4 className="mb-2 text-xs font-extrabold">
              {section.title ? l(section.title) : l({ en: 'Details', ar: 'التفاصيل' })}
            </h4>
            <table className="w-full min-w-[34rem] text-xs">
              <caption className="sr-only">
                {section.title ? l(section.title) : l({ en: 'Measured report details', ar: 'تفاصيل التقرير المقاسة' })}
              </caption>
              <thead>
                <tr className="border-b border-aeblack-100 text-start text-ink-faint">
                  {section.columns.map((column, index) => <th key={`${column.field}-${index}`} scope="col" className="px-2 py-2 text-start">{l(column.label)}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 20).map((row, index) => (
                  <tr key={index} className="border-b border-aeblack-50">
                    {section.columns.map((column, columnIndex) => (
                      <td key={`${column.field}-${columnIndex}`} className="px-2 py-2">{valueLabel(row[column.field], lang)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <p className="py-3 text-xs text-ink-faint">{l({ en: 'No measured rows.', ar: 'لا توجد صفوف مقاسة.' })}</p>}
          </div>
        );
      })}
      {links.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {links.map((section, index) => (
            <a
              key={section.id ?? `${section.href}-${index}`}
              href={section.href}
              className="aegov-btn btn-outline btn-sm inline-flex min-h-10 items-center justify-center"
            >
              {l(section.label)}
            </a>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

type AdapterState = 'connected' | 'degraded' | 'not_configured';

function adapterState(integration: { enabled: boolean; health_status: string }): AdapterState {
  if (!integration.enabled) return 'not_configured';
  if (integration.health_status === 'healthy' || integration.health_status === 'connected') return 'connected';
  if (integration.health_status === 'degraded' || integration.health_status === 'unhealthy' || integration.health_status === 'error') return 'degraded';
  return 'not_configured';
}

function AdapterStatus({ state }: { state: AdapterState }) {
  const { l } = useI18n();
  const labels: Record<AdapterState, Localized> = {
    connected: { en: 'Connected', ar: 'متصل' },
    degraded: { en: 'Degraded', ar: 'متدهور' },
    not_configured: { en: 'Not configured', ar: 'غير مهيأ' },
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="font-bold">{state === 'connected' ? '✓' : state === 'degraded' ? '!' : '—'}</span>
      <StatusPill
        state={state === 'connected' ? 'published' : state === 'degraded' ? 'in_review' : 'draft'}
        label={l(labels[state])}
      />
    </span>
  );
}

export function AssuranceCommandCenter({ slug }: { slug: string }) {
  const { l, lang } = useI18n();
  const [data, setData] = useState<AssuranceCommandCenterData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trendWindow, setTrendWindow] = useState<TrendWindowMinutes>(10_080);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    admin().assuranceCommandCenter(slug, lang)
      .then((result) => {
        if (!cancelled) setData(result.commandCenter);
      })
      .catch((reason: Error) => {
        if (!cancelled) setError(reason.message);
      });
    return () => {
      cancelled = true;
    };
  }, [lang, slug]);

  if (error) return <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{error}</div></div>;
  if (!data) return <p className="text-xs text-ink-faint">{l({ en: 'Loading command center…', ar: 'جارٍ تحميل مركز القيادة…' })}</p>;

  const integrations = Array.isArray(data.latestRun?.data.integrations)
    ? data.latestRun?.data.integrations as {
        adapter_key: string;
        display_name: string;
        enabled: boolean;
        health_status: string;
        last_checked_at: string | null;
      }[]
    : [];
  const analysis = data.latestRun?.data.analysis && typeof data.latestRun.data.analysis === 'object'
    ? data.latestRun.data.analysis as Record<string, unknown>
    : {};
  const insights = localizedItems(analysis.insights);
  const recommendations = localizedItems(analysis.recommendations);
  const windowMeasurements = TREND_WINDOWS.map((window) => ({
    ...window,
    measurement: windowMeasurement(data.reports, window.minutes),
  }));
  return (
    <section aria-labelledby="assurance-command-center-title" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 id="assurance-command-center-title" className="text-base font-extrabold">
            {l({ en: 'Assurance command center', ar: 'مركز قيادة الضمان' })}
          </h2>
          <p className="mt-1 text-xs text-ink-faint">
            {l({
              en: 'The same allowlisted definitions power this view, scheduled email and aggregate export.',
              ar: 'تغذي التعريفات المسموح بها نفسها هذا العرض والبريد المجدول والتصدير المجمع.',
            })}
          </p>
        </div>
        <div className="text-end text-[10px] text-ink-faint">
          <div>
            {l({ en: 'Data refreshed', ar: 'تحديث البيانات' })}: {' '}
            <time dateTime={data.generatedAt}>{new Date(data.generatedAt).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}</time>
          </div>
          <div>
            {l({ en: 'Latest successful measurement', ar: 'أحدث قياس ناجح' })}: {' '}
            {data.latestRun
              ? <time dateTime={data.latestRun.startedAt}>{new Date(data.latestRun.startedAt).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}</time>
              : l({ en: 'Not measured', ar: 'غير مقاس' })}
          </div>
        </div>
      </div>

      <GlassCard id="assurance-trends" className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-extrabold">{l({ en: 'Measured trends', ar: 'الاتجاهات المقاسة' })}</h3>
            <p className="mt-1 text-xs text-ink-faint">
              {l({ en: 'Window totals come from governed report bindings; the chart shows available source observations.', ar: 'تأتي إجماليات النوافذ من روابط التقارير المحكومة، ويعرض المخطط ملاحظات المصدر المتاحة.' })}
            </p>
          </div>
          <div role="group" aria-label={l({ en: 'Trend window', ar: 'نافذة الاتجاه' })} className="flex flex-wrap gap-2">
            {windowMeasurements.map((window) => (
              <button
                key={window.minutes}
                type="button"
                aria-pressed={trendWindow === window.minutes}
                onClick={() => setTrendWindow(window.minutes)}
                className={[
                  'min-h-11 rounded-lg border px-3 py-2 text-start transition',
                  trendWindow === window.minutes
                    ? 'border-seablue-400 bg-seablue-50 ring-2 ring-seablue-100'
                    : 'border-aeblack-100 bg-white hover:border-seablue-200',
                ].join(' ')}
              >
                <span className="block text-[10px] font-bold text-ink-faint">{l(window.label)}</span>
                <span className="block font-mono text-sm font-extrabold text-ink">
                  {window.measurement ? valueLabel(window.measurement.value, lang) : l({ en: 'Not measured', ar: 'غير مقاس' })}
                </span>
                {window.measurement && <span className="block text-[9px] text-ink-faint">{l(window.measurement.label)}</span>}
              </button>
            ))}
          </div>
        </div>
        <Trend rows={data.trend} generatedAt={data.generatedAt} windowMinutes={trendWindow} />
      </GlassCard>

      {data.reports.map((report) => <ReportDefinitionView key={report.definition.key} report={report} />)}

      <div className="grid gap-4 lg:grid-cols-2">
        <GlassCard className="p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-extrabold">{l({ en: 'Alert and incident drilldown', ar: 'تفاصيل التنبيهات والحوادث' })}</h3>
            <Link to={`/agents/${encodeURIComponent(slug)}?tab=audit`} className="text-xs font-bold text-oasis underline-offset-2 hover:underline">
              {l({ en: 'Open audit', ar: 'فتح التدقيق' })}
            </Link>
          </div>
          <div className="mt-3 max-h-96 space-y-2 overflow-auto">
            {data.attention.map((item, index) => (
              <details key={`${item.category}-${item.observed_at}-${index}`} className="rounded-lg border border-aeblack-100 bg-white p-3 text-xs">
                <summary className="cursor-pointer font-bold">
                  <span>{item.category.replaceAll('_', ' ')}</span>
                  <span className="ms-2 text-[10px] font-semibold text-ink-faint">
                    {item.severity ?? l({ en: 'Unclassified', ar: 'غير مصنف' })} ·{' '}
                    <time dateTime={item.observed_at}>{new Date(item.observed_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}</time>
                  </span>
                </summary>
                <dl className="mt-3 grid gap-2 rounded-lg bg-aeblack-50 p-3 sm:grid-cols-2">
                  <div><dt className="text-ink-faint">{l({ en: 'Severity', ar: 'الخطورة' })}</dt><dd className="font-bold">{item.severity ?? l({ en: 'Unclassified', ar: 'غير مصنف' })}</dd></div>
                  <div><dt className="text-ink-faint">{l({ en: 'Status', ar: 'الحالة' })}</dt><dd className="font-bold">{item.status ?? l({ en: 'Not supplied', ar: 'غير متوفر' })}</dd></div>
                  <div><dt className="text-ink-faint">{l({ en: 'Channel', ar: 'القناة' })}</dt><dd className="font-bold">{item.channel ?? l({ en: 'Not supplied', ar: 'غير متوفر' })}</dd></div>
                  <div><dt className="text-ink-faint">{l({ en: 'Observed', ar: 'وقت الرصد' })}</dt><dd><time dateTime={item.observed_at}>{new Date(item.observed_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}</time></dd></div>
                </dl>
              </details>
            ))}
            {data.attention.length === 0 && <p className="text-xs text-ink-faint">{l({ en: 'No open attention items.', ar: 'لا توجد عناصر اهتمام مفتوحة.' })}</p>}
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <h3 className="text-sm font-extrabold">{l({ en: 'Adapter health', ar: 'صحة المحولات' })}</h3>
          <div className="mt-3 space-y-2">
            {integrations.map((integration) => {
              const state = adapterState(integration);
              return (
                <div key={integration.adapter_key} className="flex items-center justify-between gap-3 rounded-lg border border-aeblack-100 p-3">
                  <div>
                    <div className="text-xs font-bold">{integration.display_name}</div>
                    <div className="text-[10px] text-ink-faint">{integration.adapter_key}</div>
                    {integration.last_checked_at && (
                      <time dateTime={integration.last_checked_at} className="text-[9px] text-ink-faint">
                        {l({ en: 'Checked ', ar: 'آخر فحص ' })}
                        {new Date(integration.last_checked_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}
                      </time>
                    )}
                  </div>
                  <AdapterStatus state={state} />
                </div>
              );
            })}
            {integrations.length === 0 && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-aeblack-100 p-3">
                <p className="text-xs text-ink-faint">{l({ en: 'No adapter observations were supplied by the latest run.', ar: 'لم يوفر آخر تشغيل ملاحظات عن المحولات.' })}</p>
                <AdapterStatus state="not_configured" />
              </div>
            )}
          </div>
        </GlassCard>
      </div>

      <GlassCard className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-extrabold">{l({ en: 'Run-backed insights', ar: 'رؤى مستندة إلى التشغيل' })}</h3>
          <Link to={`/agents/${encodeURIComponent(slug)}?tab=reporting`} className="text-xs font-bold text-oasis underline-offset-2 hover:underline">
            {l({ en: 'Manage governed reports', ar: 'إدارة التقارير المحكومة' })}
          </Link>
        </div>
        {data.latestRun ? (
          <div className="mt-3 space-y-3">
            <p className="rounded-lg bg-aeblack-50 p-3 text-sm text-ink-soft">{l(data.latestRun.summary)}</p>
            <div className="grid gap-3 lg:grid-cols-2">
              <div>
                <h4 className="text-xs font-extrabold">{l({ en: 'Insights', ar: 'الرؤى' })}</h4>
                {insights.length > 0
                  ? <ul className="mt-2 space-y-2 text-xs text-ink-soft">{insights.map((insight, index) => <li key={index}>• {l(insight)}</li>)}</ul>
                  : <p className="mt-2 text-xs text-ink-faint">{l({ en: 'No grounded insights were supplied.', ar: 'لم يتم توفير رؤى مستندة.' })}</p>}
              </div>
              <div>
                <h4 className="text-xs font-extrabold">{l({ en: 'Recommendations', ar: 'التوصيات' })}</h4>
                {recommendations.length > 0
                  ? <ul className="mt-2 space-y-2 text-xs text-ink-soft">{recommendations.map((recommendation, index) => <li key={index}>• {l(recommendation)}</li>)}</ul>
                  : <p className="mt-2 text-xs text-ink-faint">{l({ en: 'No grounded recommendations were supplied.', ar: 'لم يتم توفير توصيات مستندة.' })}</p>}
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-xs text-ink-faint">{l({ en: 'Not measured — no successful run is available.', ar: 'غير مقاس — لا يتوفر تشغيل ناجح.' })}</p>
        )}
      </GlassCard>
    </section>
  );
}

